import { ToolSchema, ToolImplementation, ToolResult } from '../tool-types';
import {
  getMetroNextStateIds,
  setCurrentMetroStateId,
  METRO_PHASE_INDEX,
  type MetroConversationStateId
} from '@/config/metro-conversation-states';
import { getMetroRealtimeInstructions } from '@/config/metro-instructions';
import { toolRegistry } from '@/tools/tool-registry';

const ALL_STATE_IDS: MetroConversationStateId[] = [
  'greeting',
  'review_current_role',
  'assess_skills',
  'identify_gaps',
  'set_goals',
  'course_search_results',
  'create_action_plan',
  'close'
];

export const stateChangeToolMetroSchema: ToolSchema = {
  type: 'function',
  name: 'set_conversation_state',
  description:
    'Switch the coaching conversation to the next state. Only call this when the exit condition is met.',
  parameters: {
    type: 'object',
    properties: {
      next_state: {
        type: 'string',
        enum: ALL_STATE_IDS,
        description: 'Next conversation state id'
      }
    },
    required: ['next_state'],
    additionalProperties: false
  }
};

export const stateChangeToolMetroImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  const { next_state: nextState } = args ?? {};

  if (!nextState || !ALL_STATE_IDS.includes(nextState)) {
    return { success: false, error: 'Invalid next_state provided.' };
  }

  setCurrentMetroStateId(nextState);
  const allowedNext = getMetroNextStateIds(nextState).join(', ') || 'none';

  if (typeof window !== 'undefined') {
    const phaseIndex = METRO_PHASE_INDEX[nextState as MetroConversationStateId] ?? -1;
    window.dispatchEvent(
      new CustomEvent('metro-phase-changed', {
        detail: {
          stateId: nextState,
          phaseIndex,
          label: nextState
        }
      })
    );
  }

  return {
    success: true,
    data: `State updated to ${nextState}. Allowed next states: ${allowedNext}.`,
    sessionUpdate: {
      instructions: getMetroRealtimeInstructions(nextState),
      tools: toolRegistry.getToolSchemas()
    }
  };
};
